import { Container, Row, Col, Badge, Alert, Card, Button, Spinner } from "react-bootstrap";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "./AuthContext";
import { apiFetch } from "./apiFetch";
import { auth } from "./firebase";

const CUSTOMER_PORTAL_URL = "https://billing.stripe.com/p/login/3cI4gy9oBd5R487aaO8so00";

const plans = [
  {
    name: "Free",
    tier: "free",
    price: "$0",
    period: "forever",
    features: ["100 requests/day", "All news sources", "Standard support"],
    variant: "outline-light" as const,
  },
  {
    name: "Plus",
    tier: "plus",
    price: "$3",
    period: "/month",
    features: [
      "1,000 requests/day",
      "All news sources",
      "Priority support",
    ],
    variant: "warning" as const,
    stripePriceId: "price_1THlQBCaCI12Al9tDUMlF7qe",
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentTier, setCurrentTier] = useState<string>("free");

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const idToken = await auth.currentUser?.getIdToken();
        const response = await apiFetch("/user/tokens", {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          setCurrentTier(data.tier || "free");
        }
      } catch {
        // ignore
      }
    })();
  }, [user]);

  const handleSubscribe = async (stripePriceId: string) => {
    if (!user) {
      alert("Please sign in first to subscribe.");
      return;
    }

    setLoading(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const response = await apiFetch("/user/create-checkout-session", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ priceId: stripePriceId }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Failed to create checkout session");
      }

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      alert(error instanceof Error ? error.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const renderButton = (plan: typeof plans[number]) => {
    const isCurrentPlan = plan.tier === currentTier;

    if (isCurrentPlan) {
      return (
        <Button variant="success" size="lg" className="w-100" disabled>
          Current Plan
        </Button>
      );
    }

    if (plan.stripePriceId) {
      return (
        <Button
          variant="primary"
          size="lg"
          className="w-100"
          disabled={loading}
          onClick={() => handleSubscribe(plan.stripePriceId!)}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Redirecting…
            </>
          ) : (
            `Subscribe to ${plan.name}`
          )}
        </Button>
      );
    }

    return (
      <Button variant="outline-secondary" size="lg" className="w-100" disabled>
        Free Plan
      </Button>
    );
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        minHeight: "100vh",
        color: "white",
      }}
    >
      <Helmet>
        <title>Pricing - Headliners News API | Free & Plus Plans</title>
        <meta name="description" content="Simple, transparent pricing for Headliners News API. Free tier with 100 requests/day or Plus at $3/month for 1,000 requests/day. All sources included, cancel anytime." />
        <link rel="canonical" href="https://headlinefeed.dev/pricing" />
      </Helmet>
      <Container className="py-5">
        {/* Header */}
        <Row className="mb-5">
          <Col>
            <div className="text-center">
              <h1 className="display-4 fw-bold mb-3">Pricing Plans</h1>
              <p className="lead">Choose the plan that fits your needs</p>
              <Badge bg="success" className="px-3 py-2 me-2">
                Simple Pricing
              </Badge>
              <Badge bg="info" className="px-3 py-2 me-2">
                No Hidden Fees
              </Badge>
              <Badge bg="warning" text="dark" className="px-3 py-2">
                Cancel Anytime
              </Badge>
            </div>
          </Col>
        </Row>

        {/* Pricing Cards */}
        <Row className="mb-5 justify-content-center">
          {plans.map((plan) => (
            <Col md={5} key={plan.name} className="mb-4">
              <Card
                className="h-100 border-0 shadow"
                style={{
                  background: "rgba(255,255,255,0.95)",
                  borderRadius: "12px",
                }}
              >
                <Card.Body className="d-flex flex-column p-4 text-dark">
                  <h3 className="fw-bold">{plan.name}</h3>
                  <div className="mb-3">
                    <span className="display-5 fw-bold">{plan.price}</span>
                    <span className="text-muted ms-1">{plan.period}</span>
                  </div>
                  <ul className="list-unstyled mb-4 flex-grow-1">
                    {plan.features.map((feature) => (
                      <li key={feature} className="mb-2">
                        <span className="text-success me-2">&#10003;</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {renderButton(plan)}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Manage Subscription */}
        {user && currentTier !== "free" && (
          <Row className="mb-4">
            <Col className="text-center">
              <Button
                variant="outline-light"
                size="lg"
                href={CUSTOMER_PORTAL_URL}
                target="_blank"
              >
                Manage Subscription
              </Button>
            </Col>
          </Row>
        )}

        {/* Usage Tips */}
        <Row>
          <Col>
            <Alert
              variant="info"
              style={{
                background: "rgba(13,202,240,0.1)",
                border: "1px solid rgba(13,202,240,0.3)",
                color: "white",
              }}
            >
              <strong>Billing Information:</strong> Your billing plan is now
              activated and ready to use.
              {user ? (
                <div className="mt-2">
                  <strong>
                    Welcome back, {user.displayName || user.email || "Developer"}
                    !
                  </strong>{" "}
                  You can manage your subscription and view billing details
                  through your account dashboard.
                  <br />
                  <small className="text-light">
                    Subscription changes take effect immediately
                  </small>
                  <br />
                  <small className="text-light">
                    All plans include API access with different rate limits
                  </small>
                </div>
              ) : (
                <div className="mt-2">
                  Sign in to view your current plan and manage your
                  subscription.
                </div>
              )}
            </Alert>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
